import { useEffect, useMemo, useRef } from 'react';
import type { CelebrationSpec, CelebrationVariant } from '../themes/types';
import { Confetti } from './Confetti';
import { DecorationLayer } from '../themes/decoration/DecorationLayer';
import { spawnFireworks } from './spawnFireworks';

interface CelebrationEffectProps {
  readonly spec: CelebrationSpec;
  readonly palette?: string[];
}

const sequentialCursors = new WeakMap<CelebrationSpec, number>();

function pickVariant(spec: CelebrationSpec): CelebrationVariant | undefined {
  const { variants, pickMode } = spec;
  if (variants.length === 0) return undefined;
  if (pickMode === 'random') {
    return variants[Math.floor(Math.random() * variants.length)];
  }
  const cursor = sequentialCursors.get(spec) ?? 0;
  const variant = variants[cursor % variants.length];
  sequentialCursors.set(spec, cursor + 1);
  return variant;
}

// Renders exactly one variant per mount. `sequential` cycles across
// successive mounts of the same spec (WeakMap-scoped cursor).
export function CelebrationEffect({ spec, palette }: CelebrationEffectProps) {
  // Freeze the picked variant to the mount so the cursor advances once
  // per <CelebrationEffect> instance (React StrictMode double-invokes
  // component bodies but preserves useMemo output between the doubled
  // renders, keeping the pick stable and the cursor honest).
  const variant = useMemo(() => pickVariant(spec), [spec]);
  const fireworksRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (variant?.kind !== 'fireworks') return;
    const target = fireworksRef.current;
    if (!target) return;
    spawnFireworks(target);
  }, [variant]);

  if (!variant) return null;

  switch (variant.kind) {
    case 'confetti':
      return palette && palette.length > 0 ? <Confetti colors={palette} /> : <Confetti />;
    case 'balloons':
      return (
        <DecorationLayer
          spec={{
            sprite: 'bubble',
            motion: 'float-up',
            density: 'high',
            speed: 'med',
            ...(palette && palette.length > 0 ? { colors: palette } : null),
          }}
        />
      );
    case 'fireworks':
      return (
        <div
          ref={fireworksRef}
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, pointerEvents: 'none' }}
        />
      );
  }
}
